(() => {
  const cleanupByRoot = new WeakMap();
  const MODEL_LOAD_TIMEOUT_MS = 30000;

  const loadModelViewerLibrary = (source) => {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!source) return Promise.reject(new Error('Missing model-viewer source.'));
    if (window.__wakeClipModelViewerPromise) return window.__wakeClipModelViewerPromise;

    const absoluteSource = new URL(source, window.location.href).href;
    let script = Array.from(document.querySelectorAll('script[data-wc-model-viewer-library]')).find(
      (candidate) => candidate.src === absoluteSource,
    );

    if (!script) {
      script = document.createElement('script');
      script.type = 'module';
      script.src = absoluteSource;
      script.dataset.wcModelViewerLibrary = '';
      script.dataset.wcModelViewerInserted = 'true';
    }

    const promise = new Promise((resolve, reject) => {
      let settled = false;
      let timeout;

      const cleanup = () => {
        if (timeout) window.clearTimeout(timeout);
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
      };

      const finish = (callback) => {
        if (settled) return;
        settled = true;
        cleanup();
        callback();
      };

      const waitForDefinition = () => {
        script.dataset.wcModelViewerLoaded = 'true';
        customElements.whenDefined('model-viewer').then(
          () => finish(resolve),
          () => finish(() => reject(new Error('The 3D viewer could not be initialized.'))),
        );
      };

      const onLoad = () => waitForDefinition();
      const onError = () => finish(() => reject(new Error('The 3D viewer library could not be loaded.')));

      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });
      timeout = window.setTimeout(
        () => finish(() => reject(new Error('The 3D viewer library timed out.'))),
        MODEL_LOAD_TIMEOUT_MS,
      );

      if (script.dataset.wcModelViewerLoaded === 'true') {
        waitForDefinition();
      } else if (!script.isConnected) {
        document.head.append(script);
      }
    });

    window.__wakeClipModelViewerPromise = promise.catch((error) => {
      window.__wakeClipModelViewerPromise = undefined;
      if (script.dataset.wcModelViewerInserted === 'true') script.remove();
      throw error;
    });

    return window.__wakeClipModelViewerPromise;
  };

  const initialize = (root) => {
    if (!root || root.dataset.wcInitialized === 'true') return;
    root.dataset.wcInitialized = 'true';

    const modelShell = root.querySelector('[data-wc-model-shell]');
    const modelMount = root.querySelector('[data-wc-model-mount]');
    const modelTemplate = root.querySelector('[data-wc-model-template]');
    const modelOpenButton = root.querySelector('[data-wc-model-open]');
    const modelOpenLabel = root.querySelector('[data-wc-model-open-label]');
    const modelLoading = root.querySelector('[data-wc-model-loading]');
    const modelError = root.querySelector('[data-wc-model-error]');
    const buyBar = root.querySelector('[data-wc-mobile-buy]');
    const hero = root.querySelector('.wc-hero-buy .wc-primary-cta') || root.querySelector('.wc-buy-form [data-wc-submit]');
    const footer = document.querySelector('footer');
    const buyDialog = root.querySelector('[data-wc-buy-dialog]');
    const buyOpenButtons = Array.from(root.querySelectorAll('[data-wc-buy-open]'));
    const buyCloseButton = root.querySelector('[data-wc-buy-close]');
    const variantSelect = root.querySelector('[data-wc-model-select]');
    const addButton = root.querySelector('[data-wc-add-button]');
    const addLabel = root.querySelector('[data-wc-add-label]');
    const compactHero = window.matchMedia('(max-width: 900px)');
    const landscapeHero = window.matchMedia('(max-width: 900px) and (max-height: 500px) and (orientation: landscape)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const observers = [];
    let model;
    let loadTimer;
    let disposed = false;
    let modelLoadingInProgress = false;
    let modelVisible = true;
    let heroVisible = true;
    let footerVisible = false;

    const setCamera = () => {
      if (!model) return;
      const radius = landscapeHero.matches ? '155%' : compactHero.matches ? '125%' : '115%';
      const theta = landscapeHero.matches ? '30deg' : compactHero.matches ? '32deg' : '35deg';
      model.setAttribute('camera-orbit', `${theta} 67deg ${radius}`);
      model.setAttribute('min-camera-orbit', `auto 50deg ${radius}`);
      model.setAttribute('max-camera-orbit', `auto 82deg ${radius}`);
    };

    const setMotion = () => {
      if (!model) return;
      const shouldRotate =
        modelShell?.classList.contains('is-loaded') &&
        modelVisible &&
        document.visibilityState === 'visible' &&
        !reducedMotion.matches;
      model.toggleAttribute('auto-rotate', Boolean(shouldRotate));
      modelShell?.classList.toggle('is-in-view', Boolean(shouldRotate));
    };

    const updateBuyBar = () => {
      if (!buyBar) return;
      const showBuyBar = !heroVisible && !footerVisible;
      buyBar.classList.toggle('is-visible', showBuyBar);
      buyBar.toggleAttribute('inert', !showBuyBar);
      buyBar.setAttribute('aria-hidden', showBuyBar ? 'false' : 'true');
    };

    const clearLoadTimer = () => {
      if (loadTimer) window.clearTimeout(loadTimer);
      loadTimer = undefined;
    };

    const setModelButtonLabel = (label) => {
      if (modelOpenLabel && label) modelOpenLabel.textContent = label;
    };

    const showModelLoading = () => {
      modelShell?.classList.add('is-loading');
      modelShell?.classList.remove('has-error');
      modelShell?.setAttribute('aria-busy', 'true');
      if (modelLoading) modelLoading.hidden = false;
      if (modelError) modelError.hidden = true;
      if (modelOpenButton) {
        modelOpenButton.hidden = false;
        modelOpenButton.setAttribute('aria-disabled', 'true');
        setModelButtonLabel(modelOpenButton.dataset.loadingLabel);
      }
    };

    const onModelLoaded = () => {
      if (disposed || modelShell?.classList.contains('is-loaded')) return;
      const shouldTransferFocus = document.activeElement === modelOpenButton;
      clearLoadTimer();
      modelLoadingInProgress = false;
      modelShell?.classList.add('is-loaded');
      modelShell?.classList.remove('is-loading', 'has-error');
      modelShell?.setAttribute('aria-busy', 'false');
      if (modelLoading) modelLoading.hidden = true;
      if (modelError) modelError.hidden = true;
      if (modelOpenButton) {
        modelOpenButton.removeAttribute('aria-disabled');
        modelOpenButton.hidden = true;
      }
      setMotion();
      if (shouldTransferFocus) {
        window.requestAnimationFrame(() => model?.focus({ preventScroll: true }));
      }
    };

    const onModelError = () => {
      if (disposed) return;
      clearLoadTimer();
      modelLoadingInProgress = false;
      if (model) {
        model.removeEventListener('load', onModelLoaded);
        model.removeEventListener('error', onModelError);
        model.remove();
        model = undefined;
      }
      modelShell?.classList.remove('is-loading', 'is-loaded', 'is-in-view');
      modelShell?.classList.add('has-error');
      modelShell?.setAttribute('aria-busy', 'false');
      if (modelLoading) modelLoading.hidden = true;
      if (modelError) modelError.hidden = false;
      if (modelOpenButton) {
        modelOpenButton.hidden = false;
        modelOpenButton.removeAttribute('aria-disabled');
        setModelButtonLabel(modelOpenButton.dataset.retryLabel);
      }
    };

    const activateModel = async () => {
      if (disposed || modelLoadingInProgress || modelShell?.classList.contains('is-loaded')) return;
      if (!modelShell || !modelMount || !modelTemplate || !modelOpenButton) return;

      modelLoadingInProgress = true;
      showModelLoading();

      await new Promise((resolve) => {
        window.requestAnimationFrame(() => window.setTimeout(resolve, 0));
      });
      if (disposed) return;

      try {
        await loadModelViewerLibrary(modelShell.dataset.viewerSrc);
        if (disposed) return;

        const candidate = modelTemplate.content.firstElementChild?.cloneNode(true);
        if (!(candidate instanceof HTMLElement)) throw new Error('The 3D viewer template is missing.');

        model = candidate;
        model.addEventListener('load', onModelLoaded, { once: true });
        model.addEventListener('error', onModelError, { once: true });
        modelMount.replaceChildren(model);
        setCamera();
        setMotion();

        const modelSource = model.dataset.modelSrc;
        if (!modelSource) throw new Error('The 3D model source is missing.');
        loadTimer = window.setTimeout(onModelError, MODEL_LOAD_TIMEOUT_MS);
        model.src = modelSource;
        if (model.loaded) onModelLoaded();
      } catch (error) {
        onModelError();
      }
    };

    const updateVariantState = () => {
      if (!variantSelect || !addButton) return;
      const hasSelection = Boolean(variantSelect.value);
      addButton.disabled = !hasSelection;
      if (addLabel) {
        addLabel.textContent = hasSelection
          ? addButton.dataset.readyLabel || addLabel.textContent
          : addButton.dataset.selectLabel || addLabel.textContent;
      }
    };

    const openBuyDialog = (event) => {
      event?.preventDefault();
      if (!buyDialog || buyDialog.open) return;
      if (typeof buyDialog.showModal === 'function') {
        buyDialog.showModal();
        document.documentElement.classList.add('wc-dialog-open');
      } else {
        buyDialog.setAttribute('open', '');
      }
      window.requestAnimationFrame(() => variantSelect?.focus());
    };

    const closeBuyDialog = () => {
      if (!buyDialog?.open) return;
      if (typeof buyDialog.close === 'function') {
        buyDialog.close();
      } else {
        buyDialog.removeAttribute('open');
      }
    };

    const onDialogClosed = () => document.documentElement.classList.remove('wc-dialog-open');

    const closeBuyDialogFromBackdrop = (event) => {
      if (event.target === buyDialog) closeBuyDialog();
    };

    modelOpenButton?.addEventListener('click', activateModel);
    buyOpenButtons.forEach((button) => button.addEventListener('click', openBuyDialog));
    buyCloseButton?.addEventListener('click', closeBuyDialog);
    buyDialog?.addEventListener('click', closeBuyDialogFromBackdrop);
    buyDialog?.addEventListener('close', onDialogClosed);
    variantSelect?.addEventListener('change', updateVariantState);
    compactHero.addEventListener('change', setCamera);
    landscapeHero.addEventListener('change', setCamera);
    reducedMotion.addEventListener('change', setMotion);
    document.addEventListener('visibilitychange', setMotion);
    updateVariantState();

    if (modelShell && 'IntersectionObserver' in window) {
      const modelObserver = new IntersectionObserver(
        ([entry]) => {
          modelVisible = entry.isIntersecting;
          setMotion();
        },
        { threshold: 0.01 },
      );
      modelObserver.observe(modelShell);
      observers.push(modelObserver);
    }

    if (hero && buyBar && 'IntersectionObserver' in window) {
      updateBuyBar();
      const heroObserver = new IntersectionObserver(
        ([entry]) => {
          heroVisible = entry.isIntersecting;
          updateBuyBar();
        },
        { threshold: 0.1 },
      );
      heroObserver.observe(hero);
      observers.push(heroObserver);

      if (footer) {
        const footerObserver = new IntersectionObserver(
          ([entry]) => {
            footerVisible = entry.isIntersecting;
            updateBuyBar();
          },
          { threshold: 0.01 },
        );
        footerObserver.observe(footer);
        observers.push(footerObserver);
      }
    }

    cleanupByRoot.set(root, () => {
      disposed = true;
      clearLoadTimer();
      observers.forEach((observer) => observer.disconnect());
      modelOpenButton?.removeEventListener('click', activateModel);
      compactHero.removeEventListener('change', setCamera);
      landscapeHero.removeEventListener('change', setCamera);
      reducedMotion.removeEventListener('change', setMotion);
      document.removeEventListener('visibilitychange', setMotion);
      buyOpenButtons.forEach((button) => button.removeEventListener('click', openBuyDialog));
      buyCloseButton?.removeEventListener('click', closeBuyDialog);
      buyDialog?.removeEventListener('click', closeBuyDialogFromBackdrop);
      buyDialog?.removeEventListener('close', onDialogClosed);
      onDialogClosed();
      variantSelect?.removeEventListener('change', updateVariantState);
      closeBuyDialog();
      model?.removeEventListener('load', onModelLoaded);
      model?.removeEventListener('error', onModelError);
      model?.remove();
      model = undefined;
      modelMount?.replaceChildren();
      modelShell?.classList.remove('is-loading', 'is-loaded', 'has-error', 'is-in-view');
      modelShell?.setAttribute('aria-busy', 'false');
      if (modelLoading) modelLoading.hidden = true;
      if (modelError) modelError.hidden = true;
      if (modelOpenButton) {
        modelOpenButton.hidden = false;
        modelOpenButton.removeAttribute('aria-disabled');
        setModelButtonLabel(modelOpenButton.dataset.openLabel);
      }
      root.dataset.wcInitialized = 'false';
    });
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-wc-home]').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-wc-home]').forEach((root) => cleanupByRoot.get(root)?.());
  });
})();
